import React from 'react';
import { CollapsibleGroup } from '@/components/CollapsibleGroup';
import { CountryFlag } from '@/components/CountryFlag';
import type { CountryGroup, CountryLeague } from '@/utils/leagueCountries';
import styles from './styles.module.scss';

export interface CountryLeaguesGroupProps<T extends CountryLeague> {
  group: CountryGroup<T>;
  expanded: boolean;
  onToggle: () => void;
  /** Renders one league row (usually a `LeagueRow`); `showSeason` disambiguates same-name leagues. */
  renderRow: (league: T, showSeason: boolean) => React.ReactNode;
  /**
   * Skip rendering the rows while collapsed — for long lists (hundreds of
   * leagues). Costs the collapse animation, so short lists leave it off.
   */
  lazyBody?: boolean;
}

/**
 * One country card of a league index: a collapsible card whose whole header
 * (flag or international trophy, localized name, league count, caret)
 * toggles the list of league rows supplied by the caller.
 */
export function CountryLeaguesGroup<T extends CountryLeague>({
  group,
  expanded,
  onToggle,
  renderRow,
  lazyBody = false,
}: CountryLeaguesGroupProps<T>) {
  const showRows = expanded || !lazyBody;

  return (
    <CollapsibleGroup
      headerAsToggle
      id={`country-group-${group.key}`}
      title={(
        <span className={styles.heading}>
          <CountryFlag src={group.flagUrl} international={group.international} />
          <span className={styles.country} data-testid={`country-group-name-${group.key}`}>
            {group.name}
          </span>
        </span>
      )}
      count={group.leagues.length}
      expanded={expanded}
      onToggle={onToggle}
      testId={`country-group-${group.key}`}
      toggleTestId={`country-group-toggle-${group.key}`}
    >
      {showRows && group.leagues.map(league => (
        <React.Fragment key={league.id}>
          {renderRow(league, group.seasonShownIds.has(league.id))}
        </React.Fragment>
      ))}
    </CollapsibleGroup>
  );
}

export default CountryLeaguesGroup;
